import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Loader2, Search, Inbox } from "lucide-react";
import { API, authHeaders, formatErr } from "./api";
import { inputCls, TabCard } from "./fields";
import { WaIcon } from "../../components/CTA";

const STATUSES = ["New", "Contacted", "Quoted", "Won", "Lost"];

const statusCls = {
  New: "text-pop border-myrtle/40 bg-myrtle/10",
  Contacted: "text-blue-300 border-blue-400/30 bg-blue-400/10",
  Quoted: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  Won: "text-green-300 border-green-400/30 bg-green-400/10",
  Lost: "text-mut border-line bg-base",
};

const waNumber = (phone) => {
  const d = String(phone || "").replace(/\D/g, "");
  return d.length === 10 ? `91${d}` : d;
};

export default function LeadsTab({ token, onUnauthorized, onLeadsSeen }) {
  const [leads, setLeads] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = () => {
    axios
      .get(`${API}/leads`, authHeaders(token))
      .then((r) => setLeads(r.data.leads))
      .catch((err) => {
        if (err.response?.status === 401) return onUnauthorized();
        toast.error("Couldn't load leads.");
      });
  };
  useEffect(() => {
    load();
    onLeadsSeen?.();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!leads) return [];
    const needle = q.trim().toLowerCase();
    return leads.filter((l) => {
      if (status && l.status !== status) return false;
      if (needle && !(l.name || "").toLowerCase().includes(needle) && !(l.phone || "").includes(needle)) return false;
      return true;
    });
  }, [leads, status, q]);

  const patch = async (id, updates) => {
    setBusyId(id);
    try {
      const { data } = await axios.patch(`${API}/leads/${id}`, updates, authHeaders(token));
      setLeads(leads.map((l) => (l.id === id ? { ...l, ...data } : l)));
      if (updates.status) toast.success(`Marked as ${updates.status}.`);
    } catch (err) {
      if (err.response?.status === 401) return onUnauthorized();
      toast.error(formatErr(err, "Update failed."));
    } finally {
      setBusyId("");
    }
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replaceAll('"', '""')}"`;
    const rows = [
      ["Date", "Name", "Phone", "Email", "Business Type", "Need", "Message", "Status", "Notes"].map(esc).join(","),
      ...filtered.map((l) => [l.created_at, l.name, l.phone, l.email || "", l.business_type, l.need, l.message, l.status, l.notes].map(esc).join(",")),
    ];
    const url = URL.createObjectURL(new Blob([rows.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `allcov-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} leads.`);
  };

  return (
    <TabCard title="Leads" sub="Every contact form submission — newest first" testid="tab-leads">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-faint" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or phone" data-testid="leads-search" className={`${inputCls} pl-10`} />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="leads-filter" className={`${inputCls} w-auto`}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={exportCsv} data-testid="leads-export" className="flex items-center gap-2 border border-line px-5 py-3 text-xs uppercase tracking-[0.15em] text-mut transition-colors duration-300 hover:border-myrtle hover:text-pop">
          <Download size={14} /> Export CSV
        </button>
      </div>

      {!leads ? (
        <p className="flex items-center gap-2 text-sm text-mut"><Loader2 size={14} className="animate-spin" /> Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-mut" data-testid="leads-empty"><Inbox size={14} className="text-faint" /> No leads match.</p>
      ) : (
        <div className="space-y-4">
          {filtered.map((l) => (
            <div key={l.id} className="border border-line bg-base p-5" data-testid={`lead-${l.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-batangas text-lg tracking-tight text-txt">{l.name}</p>
                  <p className="mt-0.5 text-xs text-faint">
                    {l.created_at ? new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : ""}
                  </p>
                </div>
                <span className={`border px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${statusCls[l.status] || statusCls.New}`} data-testid={`lead-status-${l.id}`}>{l.status}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-mut">
                <a href={`tel:+${waNumber(l.phone)}`} className="transition-colors hover:text-pop" data-testid={`lead-phone-${l.id}`}>{l.phone}</a>
                {l.email && <a href={`mailto:${l.email}`} className="transition-colors hover:text-pop">{l.email}</a>}
                {l.business_type && <span>{l.business_type}</span>}
                {l.need && <span className="text-faint">Needs: {l.need}</span>}
              </div>
              {l.message && <p className="mt-3 border-l-2 border-line pl-3 text-sm leading-relaxed text-mut">{l.message}</p>}
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <select
                  value={l.status}
                  disabled={busyId === l.id}
                  onChange={(e) => patch(l.id, { status: e.target.value })}
                  data-testid={`lead-status-select-${l.id}`}
                  className={`${inputCls} w-auto py-2 text-xs`}
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <a
                  href={`https://wa.me/${waNumber(l.phone)}?text=${encodeURIComponent(`Hi ${l.name}, this is Kaushlesh from Allcov — following up on your website enquiry.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`lead-wa-${l.id}`}
                  className="flex items-center gap-2 bg-myrtle px-4 py-2 text-xs uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-myrtle-light"
                >
                  <WaIcon size={13} /> Reply on WhatsApp
                </a>
              </div>
              <div className="mt-3 flex items-start gap-2">
                <textarea
                  rows={1}
                  value={l.notes || ""}
                  onChange={(e) => setLeads(leads.map((x) => (x.id === l.id ? { ...x, notes: e.target.value } : x)))}
                  placeholder="Private notes — only you see this"
                  data-testid={`lead-notes-${l.id}`}
                  className={`${inputCls} resize-none py-2 text-xs`}
                />
                <button
                  onClick={() => patch(l.id, { notes: l.notes || "" })}
                  disabled={busyId === l.id}
                  data-testid={`lead-notes-save-${l.id}`}
                  className="shrink-0 border border-line px-4 py-2 text-xs uppercase tracking-[0.15em] text-mut transition-colors duration-300 hover:border-myrtle hover:text-pop disabled:opacity-60"
                >
                  Save
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </TabCard>
  );
}
